import { HttpErrorKind } from "@kalodata/http";
import type { HttpErrorShape, HttpPlugin } from "@kalodata/http";
import { reportApiError } from "@kalodata/sentry";
import type { ApiErrorInput } from "@kalodata/sentry";
import type { ClipHttpMeta, ClipHttpContext } from "./types";
import { isNotNil } from "@kalodata/tools";

const FILTERED_VALUE = "[Filtered]";
const FORM_DATA_VALUE = "[FormData]";
const CIRCULAR_VALUE = "[Circular]";
const UNSERIALIZABLE_VALUE = "[Unserializable]";

const FULL_REDACT_KEYS = new Set([
  "password",
  "token",
  "accesstoken",
  "refreshtoken",
  "authorization",
  "credential",
  "code",
  "smscode",
  "emailcode",
  "tccode",
  "response",
  "captcha",
]);

const PARTIAL_MASK_KEYS = new Set(["phonenumber", "email"]);

type SentryReporter = (input: ApiErrorInput) => void;

/**
 * 不可操作、不应上报 Sentry 的 HTTP 错误：
 *  - transport / timeout / abort：用户侧网络或主动取消（线上高量：Failed to fetch、Load failed、AbortError 等），非服务端故障。
 *  - 业务态 / 鉴权状态码：400(用户输入非法，如非法商品链接)、401(未登录)、403(无权限)、404(资源不存在)、429(限流)。
 *    这些由用户行为或权限决定，非可操作 bug。（包内 reportApiError 已额外丢弃 502。）
 *  - 业务态错误（kind: Business）：后端 HTTP 成功（如 200）但 `success: false` 的预期响应
 *    （验证码错误 CAPTCHA_ERROR、密码错误、限流提示等），由 UI toast 反馈给用户，非可操作 bug。
 *    仅当伴随服务端错误状态（status >= 500，疑似后端故障）时才上报。
 */
const NON_REPORTABLE_STATUSES = new Set([400, 401, 403, 404, 429]);

const NON_REPORTABLE_KINDS = new Set<HttpErrorKind>([
  HttpErrorKind.Transport,
  HttpErrorKind.Timeout,
  HttpErrorKind.Aborted,
]);

export const shouldReportHttpError = (error: HttpErrorShape<unknown, ClipHttpMeta, ClipHttpContext>): boolean => {
  if (isNotNil(error.kind) && NON_REPORTABLE_KINDS.has(error.kind)) return false;
  if (error.kind === HttpErrorKind.Business && (error.status == null || error.status < 500)) return false;
  if (isNotNil(error.status) && NON_REPORTABLE_STATUSES.has(error.status)) return false;
  return true;
};

const normalizeKey = (key: string) => key.replace(/[\s_-]/g, "").toLowerCase();

const isFullRedactKey = (key: string) => FULL_REDACT_KEYS.has(normalizeKey(key));

const isPartialMaskKey = (key: string) => PARTIAL_MASK_KEYS.has(normalizeKey(key));

const maskGeneric = (value: string) => {
  if (!value) return value;
  if (value.length <= 2) return `${value.slice(0, 1)}***`;
  if (value.length <= 4) return `${value.slice(0, 1)}***${value.slice(-1)}`;

  const prefixLength = Math.min(2, Math.floor(value.length / 3));
  const suffixLength = Math.min(2, Math.floor(value.length / 3));
  return `${value.slice(0, prefixLength)}***${value.slice(-suffixLength)}`;
};

const maskEmail = (value: string) => {
  const atIndex = value.indexOf("@");
  if (atIndex <= 0) return maskGeneric(value);

  const localPart = value.slice(0, atIndex);
  const domain = value.slice(atIndex);

  if (localPart.length <= 1) return `${localPart.slice(0, 1)}***${domain}`;
  if (localPart.length <= 4) return `${localPart.slice(0, 1)}***${localPart.slice(-1)}${domain}`;

  return `${localPart.slice(0, 2)}***${localPart.slice(-2)}${domain}`;
};

const maskPhone = (value: string) => {
  const digitCount = value.replace(/\D/g, "").length;
  if (digitCount <= 7) return maskGeneric(value);

  let seenDigits = 0;
  return value.replace(/\d/g, (digit) => {
    seenDigits += 1;
    return seenDigits <= 3 || seenDigits > digitCount - 4 ? digit : "*";
  });
};

const maskPartialValue = (key: string, value: string | number) => {
  const stringValue = String(value);
  const normalizedKey = normalizeKey(key);

  if (normalizedKey === "email") return maskEmail(stringValue);
  if (normalizedKey === "phonenumber") return maskPhone(stringValue);

  return maskGeneric(stringValue);
};

const isPlainObject = (value: unknown): value is Record<string, unknown> => {
  if (Object.prototype.toString.call(value) !== "[object Object]") return false;
  const prototype = Object.getPrototypeOf(value);
  return prototype === Object.prototype || prototype === null;
};

export const redactSentryPayload = (value: unknown, seen = new WeakSet<object>()): unknown => {
  try {
    if (value === null || value === undefined || typeof value !== "object") return value;

    if (typeof FormData !== "undefined" && value instanceof FormData) {
      return FORM_DATA_VALUE;
    }

    if (seen.has(value)) return CIRCULAR_VALUE;
    seen.add(value);

    if (Array.isArray(value)) {
      try {
        return value.map((item) => redactSentryPayload(item, seen));
      } finally {
        seen.delete(value);
      }
    }

    try {
      if (!isPlainObject(value)) {
        return `[${value.constructor?.name || "Object"}]`;
      }

      return Object.keys(value).reduce<Record<string, unknown>>((result, key) => {
        if (isFullRedactKey(key)) {
          result[key] = FILTERED_VALUE;
          return result;
        }

        if (isPartialMaskKey(key)) {
          const fieldValue = value[key];
          result[key] =
            typeof fieldValue === "string" || typeof fieldValue === "number"
              ? maskPartialValue(key, fieldValue)
              : FILTERED_VALUE;
          return result;
        }

        try {
          result[key] = redactSentryPayload(value[key], seen);
        } catch {
          result[key] = UNSERIALIZABLE_VALUE;
        }

        return result;
      }, {});
    } finally {
      seen.delete(value);
    }
  } catch {
    return UNSERIALIZABLE_VALUE;
  }
};

export const createSentryPlugin = (
  reporter: SentryReporter = reportApiError,
): HttpPlugin<ClipHttpMeta, ClipHttpContext> => {
  return {
    name: "sentry",
    onError: (error, context) => {
      if (!shouldReportHttpError(error)) {
        throw error; // 不上报，但保持错误传播
      }

      const request = error.request || context.request;
      const responseData = error.response?.data as { code?: string | number; message?: string } | undefined;

      try {
        reporter({
          platform: "kaloclip",
          url: request.url,
          method: request.method,
          status: error.status,
          code: responseData?.code,
          message: error.message,
          traceID: error.traceId,
          requestData: redactSentryPayload(request.body),
          responseData: redactSentryPayload(error.response?.data),
          originalError: error,
        });
      } catch {}

      throw error; // Re-throw to maintain error propagation
    },
  };
};
